import React from 'react'
import { FormattedTime } from 'react-intl'

import T from 'components/FormattedMessage'
import Layer from 'components/Layer'
import Button from 'components/Button'
import Currency from 'components/Currency'
import LabeledValue from 'components/LabeledValue'
import NavigationHeader from 'components/NavigationHeader'
import UserPhoto from 'components/UserPhoto'

import { Typography, Grid, withStyles, WithStyles, createStyles, withMobileDialog, Theme } from '@material-ui/core'

import { GameDetailsResponse, UserInfoResponse, EditInvitationResponse } from 'protos'
import { Action } from 'types'

interface Props {
  getGameDetails: () => Promise<GameDetailsResponse>
  declineInvitation: () => Promise<EditInvitationResponse>
  acceptInvitation: () => void
  onClose: () => void
  userInfo: UserInfoResponse
}

interface State {
  gameDetails: Action<GameDetailsResponse | null>
}

const styles = (theme: Theme) => createStyles({
  hostImage: {
    margin: `${2 * theme.spacing()}px auto 0`,
  },
  details: {
    margin: `${3 * theme.spacing()}px 0`,
  },
  matchContainer: {
    padding: 2 * theme.spacing(),
    borderWidth: '1px 0',
    borderStyle: 'solid',
    borderColor: theme.palette.grey[300],
  },
  userSlideshow: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: `${3 * theme.spacing()}px ${theme.spacing()}px 0`,
  },
  userItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing(),
  },
  userImage: {
    margin: `0 ${theme.spacing()}px ${theme.spacing()}px`,
  },
  buttonsContainer: {
    padding: 3 * theme.spacing(),
  },
})

type InjectedProps = Props & WithStyles<typeof styles> & { fullScreen: boolean }

class GameInviteLayer extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)
    this.state = {
      gameDetails: { isLoading: true },
    }
  }

  componentDidMount() {
    this.getGameDetails()
  }

  private getGameDetails = () => {
    this.setState(
      { gameDetails: { isLoading: true } },
      () => {
        this.props.getGameDetails()
          .then((result: GameDetailsResponse) => {
            this.setState({ gameDetails: { isLoading: false, result } })
          })
          .catch((err: Error) => {
            this.setState({ gameDetails: { isLoading: false, errorCode: err.message } })
          })
      },
    )
  }

  render() {
    const { userInfo, onClose, acceptInvitation, declineInvitation, fullScreen } = this.props
    const { result: gameDetails } = this.state.gameDetails

    if (!gameDetails || !gameDetails.game || !gameDetails.game.match || !gameDetails.game.host) {
      return null
    }

    const balance = userInfo.user!.balance![0]
    const hostUser = gameDetails.game.host
    const match = gameDetails.game.match

    const playerCount = gameDetails.leaderboard.filter(item => !item.pending).length
    const isBalanceSufficient = Number(balance!.value) >= Number(gameDetails.game.entryFee!.value)

    return (
      <Layer
        open
        maxWidth="sm"
        fullScreen={fullScreen}
        width={fullScreen ? '100%' : undefined}
        onClose={onClose}
      >
        <NavigationHeader
          position="static"
          onBackClick={onClose}
          title={<><T id="common_text_balance" /> <Currency {...balance} /></>}
          style={{ backgroundColor: '#fff' }}
        />

        <UserPhoto size="large" src={hostUser.photoUrl!} className={this.props.classes.hostImage} />

        <Typography variant="h5" align="center">
          <T id="game_invitation_subtitle" values={{ name: hostUser.name }} />
        </Typography>

        <Grid container className={this.props.classes.details}>
          <Grid item xs>
            <LabeledValue
              labelVariant="h3"
              label={<T id="game_entryfee" />}
              value={<Currency {...gameDetails.game.entryFee} />}
            />
          </Grid>
          <Grid item xs>
            <LabeledValue
              labelVariant="h3"
              label={<T id="game_totalpot" />}
              value={<Currency {...gameDetails.game.maxPot} />}
            />
          </Grid>
          <Grid item xs>
            <LabeledValue
              labelVariant="h3"
              label={<T id="game_playercount" />}
              value={playerCount}
            />
          </Grid>
        </Grid>

        <div className={this.props.classes.matchContainer}>
          <Grid container direction="row" alignItems="center">
            <Grid item xs={4} sm={5}>
              <Typography variant="body2" align="right">
                {match.homeTeam!.name}
              </Typography>
            </Grid>
            <Grid item xs={4} sm={2}>
              <Typography variant="body1" align="center">
                <FormattedTime value={Number(match.kickoff) * 1000} />
              </Typography>
            </Grid>
            <Grid item xs={4} sm={5}>
              <Typography variant="body2">
                {match.awayTeam!.name}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <div className={this.props.classes.userSlideshow}>
          {(gameDetails.leaderboard || [])
            .filter(entry => entry.user)
            .map(entry => entry.user!)
            .map((user, i) => (
              <div key={i} className={this.props.classes.userItem}>
                <UserPhoto src={user.photoUrl!} className={this.props.classes.userImage} />
                <Typography variant="h3" color="inherit">{user.name}</Typography>
              </div>
            ))
          }
        </div>

        <div className={this.props.classes.buttonsContainer}>
          <Grid container spacing={2}>
            <Grid item xs>
              <Button
                fullWidth
                color="dangerous"
                variant="contained"
                onClick={declineInvitation}
              >
                <T id="invitation_action_decline" />
              </Button>
            </Grid>
            <Grid item xs>
              {isBalanceSufficient
                ? (
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    onClick={acceptInvitation}
                  >
                    <T id="invitation_action_accept" />
                  </Button>
                )
                : (
                  <Button
                    fullWidth
                    color="secondary"
                    variant="contained"
                    to="/store"
                  >
                    <T id="invitation_action_buykoins" />
                  </Button>
                )
              }
            </Grid>
          </Grid>
        </div>
      </Layer>
    )
  }
}

// TODO: https://next.material-ui.com/demos/dialogs/#responsive-full-screen
// tslint:disable-next-line: no-any
export default withStyles(styles)(withMobileDialog<Props>()(GameInviteLayer as any))
